import React, { useState, useEffect, useMemo, ChangeEvent, useRef } from "react";
import { zipObject, isEqual, filter, sortBy, findKey, last } from "lodash";
import classNames from "classnames";
import { generateMeetingName } from "@util/scheduling.util";

import {
  Button,
  TextField,
  TextareaAutosize,
  Checkbox,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
} from "@material-ui/core";

import pluralize from "pluralize";

// Components
import { CenteredCard } from "@ui/card";
import { CwRadio, TwoRowRadioOption } from "@ui/cw-radio";
import { CwDialogConfirmRed } from "@ui/cw-dialog";
import {
  Edit,
  Timelapse,
  CalendarToday,
  RemoveRedEye,
  Check,
  Notes,
  Videocam,
  WorkOutline,
  ArrowBackIos,
  Close,
} from "@material-ui/icons";
import { useSetupLinkStyles, newLinkTopSectionStyles, MeetingSettings, LinkEditor } from "./";
import { MEETING_LOCATION_DETAILS } from "@ui/scheduling-confirmation/event-summary";
import { useConfirmBeforeUnloadEvent, CONFIRM_UNSAVED_ONUNLOAD_TEXT } from "@util/react.util";

// types
import {
  BookableHoursEnum,
  LinkLocationType,
  AvailabilityType,
  PeriodType,
} from "@clockwise/schema";

import { useSpring, animated, config, useTransition } from "@react-spring/web";
import { RedButton } from "@ui/red-button";
import { useWindowSize } from "@util/react.util";
import { sanitizeSlug, validateSlug, getSchedulingLinkUrl } from "@util/scheduling.util";
import { formatDuration } from "@util/duration.util";
import { AVAILABILITY_TEXT } from "@ui/scheduling-links/constants";
import { TrackingEvents, useTracking, useTrackPageview } from "@util/analytics.util";
import { ZoomLogo } from "@components/svgs/ZoomLogo";
import { getServerUrl } from "@util/post-message-common.util";
import { paths } from "@clockwise/client-commons/src/constants/site";
import { WarningTooltip } from "@ui/scheduling-links/WarningTooltip";

export interface SetupSchedulingLinkProps {
  currentLinkSettings: MeetingSettings | null;
  canUseZoom: boolean;
  timeZone: string;
  onReturnToDashboard: () => void;
  onCreateUpdate: (linkSettings: MeetingSettings) => void;
  onDelete: (meetingSettingsId: string) => void;
  createUpdateError?: string | null;
  saving: boolean;
  linkName?: string;
  createdNewLink: boolean;
  deleting: boolean;
  clearError: () => void;
  onPreview: () => void;
  givenName?: string | null;
  familyName?: string | null;
}

interface NewLinkTopSectionProps {
  onCreateNew: () => void;
  enableCreateBtn: boolean;
  creating: boolean;
  onReturnToDashboard: () => void;
}

const DURATIONS = [15, 30, 45, 60, 90, 120, 150];
const DEFAULT_BOOKING_TIME_BUFFER = 4; // In hours.
const deleteHeader = "Are you sure you want to delete this Clockwise Link?";
const deleteBody = "Doing so will make this link inoperable for those you have shared it with.";
const MAX_DESC_LENGTH = 5000; // 5,000 characters

const transitionSpring = {
  from: { opacity: 0, translateY: -8 },
  enter: { opacity: 1, translateY: 0 },
  delay: 50,
  trail: 180,
  config: config.stiff,
};

const getDefaultLinkSettings = (
  timeZone: string,
  canUseZoom: boolean,
  name = "",
): MeetingSettings => ({
  __typename: "MeetingSettings",
  id: null,
  name,
  slug: "",
  description: "",
  locationSettings: {
    __typename: "LocationSettings",
    locationType: canUseZoom ? LinkLocationType.Zoom : LinkLocationType.Meet,
    locationDetails: null,
  },
  bookableHours: BookableHoursEnum.MeetingHours,
  availabilityType: AvailabilityType.BalanceAvailabilityAndFocus,
  isActive: true,
  bookOnNoMeetingDay: false,
  highlightBestTimes: true,
  allowFifteenMinuteMarkBooking: true,
  bookingTimeBufferHours: 4,
  timingSettings: {
    __typename: "TimingSettings",
    durationMinutes: 30,
    periodType: PeriodType.RollingWindow,
    timeRange: {
      __typename: "LinkTimeRange",
      windowSizeDays: 14,
      timeZone,
      rangeStartTimeMillis: null,
      rangeEndTimeMillis: null,
    },
  },
});

const NewLinkTopSection = ({
  onCreateNew,
  enableCreateBtn,
  creating,
  onReturnToDashboard,
}: NewLinkTopSectionProps) => {
  const classes = newLinkTopSectionStyles();

  // TODO(cw-shane) make the animation between creating a link and it becoming an actual link.
  return (
    <>
      <div className="cw-flex cw-gap-2 cw-items-center">
        <Button
          className="cw-text-slate-standard"
          onClick={onReturnToDashboard}
          cw-id="back-button"
        >
          <ArrowBackIos />
          <span>MY LINKS</span>
        </Button>
      </div>
      <span
        className="cw-text-slate-standard cw-font-semibold cw-font-brand"
        cw-id="new-link-header"
      >
        New Scheduling Link
      </span>
      <Button
        className={classes.createBtn}
        onClick={onCreateNew}
        color="primary"
        disabled={!enableCreateBtn || creating}
        variant="contained"
        cw-id="create-link-button"
      >
        {/* {creating ? (
          <CircularProgress size={18} color="primary" className={classes.loadingSpinner} />
        ) : ( */}
        <Check style={{ marginRight: 8 }} />
        {/* )} */}
        Create Link
      </Button>
    </>
  );
};

function LocationMenuItem({ locationType }: { locationType: LinkLocationType }) {
  const locationDetails = MEETING_LOCATION_DETAILS[locationType];
  const icon = locationDetails?.locationIcon;
  return (
    <div className="cw-flex cw-items-center">
      {icon && <span className="cw-mb-[-4px]">{icon}</span>}
      <span className="cw-ml-[7px]">{locationDetails?.locationName}</span>
    </div>
  );
}

export const SetupSchedulingLink = ({
  currentLinkSettings,
  canUseZoom,
  timeZone,
  onReturnToDashboard,
  onCreateUpdate,
  onDelete,
  saving,
  linkName,
  createdNewLink,
  deleting,
  createUpdateError,
  clearError,
  onPreview,
  givenName,
  familyName,
}: SetupSchedulingLinkProps) => {
  const trackEvent = useTracking();
  const classes = useSetupLinkStyles();

  const DEFAULT_LINK_SETTINGS = useMemo(
    () =>
      getDefaultLinkSettings(timeZone, canUseZoom, generateMeetingName(30, givenName, familyName)),
    [timeZone, canUseZoom, givenName, familyName],
  );

  ////////////////// State //////////////////

  const { width: windowWidth } = useWindowSize();
  const [newLinkSettings, setNewLinkSettings] = useState({
    ...(currentLinkSettings || DEFAULT_LINK_SETTINGS),
  });

  /**
   * Update the default if it changes before we have changed settings (i.e. data has loaded and altered the defaults)
   */
  useEffect(() => {
    if (!currentLinkSettings) {
      setNewLinkSettings(DEFAULT_LINK_SETTINGS);
    }
  }, [DEFAULT_LINK_SETTINGS, currentLinkSettings]);

  const [hasChanges, setHasChanges] = useState(false);

  useConfirmBeforeUnloadEvent(hasChanges);
  const [enableBookingTimeBuffer, setEnableBookingTimeBuffer] = useState(true);
  // This stores the last value of the booking time buffer, for the case where the user toggles off the feature and we need
  // to set newLinSettings.bookingTimeBufferHours to zero while maintaining a non-zero value in the picker. Will be able to
  // remove this when we add a boolean to the LinkSettings to set the bookingTimeBuffer feature on and off rather than just
  // setting the value to zero.
  const [prevTimeBuffer, setPrevTimeBuffer] = useState(
    newLinkSettings.bookingTimeBufferHours || DEFAULT_BOOKING_TIME_BUFFER, // Default to 4 hours if the feature is disabled via bookingTimeBufferHours set to 0.
  );
  const [showMore, setShowMore] = useState(!!currentLinkSettings);
  const [expandMeetingName, setExpandTitle] = useState(!!currentLinkSettings);
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const [expandDescription, setExpandDescription] = useState<boolean>(
    !!currentLinkSettings?.description,
  );

  const syncableMeetingNames = useMemo<Record<string, number>>(
    () => ({
      ...zipObject(
        DURATIONS.map((d) => generateMeetingName(d, givenName, familyName, true) + " meeting"),
        DURATIONS,
      ),
      ...{
        // Add in two custom possible names to match to support syncing "60 minute" and "90 minute" meeting names.
        ["60 minute meeting"]: 60,
        ["90 minute meeting"]: 90,
      },
    }),
    [givenName, familyName],
  );
  /**
   * Set UI to what we want when viewing an existing link.
   */
  useEffect(() => {
    if (currentLinkSettings) {
      setShowMore(true);
      setExpandTitle(true);
      if (currentLinkSettings.description) {
        setExpandDescription(true);
      }
    }
  }, [currentLinkSettings]);

  const [deleteLinkDialogOpen, setDeleteLinkDialogOpen] = useState(false);
  const [slugError, setSlugError] = useState("");
  const [saveHovered, setWarningTooltipOpen] = useState(false);

  ////////////////// Effects //////////////////

  useTrackPageview(TrackingEvents.LINKS.EDIT_PAGE.VIEW, {
    meetingSettingsId: currentLinkSettings?.id,
  });

  // After saving settings or creating a new link, currentLinkSettings gets updated. Copy it to
  useEffect(() => {
    if (currentLinkSettings) {
      setNewLinkSettings({ ...currentLinkSettings });
    }
  }, [currentLinkSettings]);

  // Set have changes if the new link settings are different from the current link settings.
  useEffect(() => {
    setHasChanges(!!currentLinkSettings && !isEqual(currentLinkSettings, newLinkSettings));
  }, [currentLinkSettings, newLinkSettings]);

  // Keep enableBookingTimeBuffer in sync with newLinkSettings.bookingTimeBufferHours.
  useEffect(() => {
    // Will be able to remove once we have this boolean saved to the LinkSettings.
    setEnableBookingTimeBuffer(newLinkSettings.bookingTimeBufferHours === 0 ? false : true);
  }, [newLinkSettings.bookingTimeBufferHours]);

  // This setter function ensures that setNewLinkSettings is used to update state, rather than
  // directly settings fields on newLinkSettings. Ensures that the proper keys and their respective
  // values types are used.
  const setLinkSetting = <KEY extends keyof MeetingSettings>(
    key: KEY,
    value: MeetingSettings[KEY],
  ) => {
    setNewLinkSettings((prevNewLinkSettings) => ({
      ...prevNewLinkSettings,
      [key]: value,
    }));
  };

  ////////////////// Animations //////////////////

  const leftTransition = useTransition(["name and description", "timing"], transitionSpring);
  const rightTransition = useTransition(
    showMore ? ["focus-mode", "limits", "best", "delete"] : ["focus-mode", "show-more"],
    showMore ? transitionSpring : { ...transitionSpring, ...{ delay: 350 } },
  );
  const leftToRightTransition = useTransition(["left", "divider", "right"], transitionSpring);

  const [saveOrViewLink, setSaveOrViewLink] = useSpring(() => ({ opacity: 0 }));
  useEffect(() => {
    setSaveOrViewLink({ opacity: 1, from: { opacity: 0 }, config: config.slow });
  }, [hasChanges, setSaveOrViewLink]);

  const bestIconStyle = useSpring({
    opacity: newLinkSettings.highlightBestTimes ? 1 : 0,
    config: config.stiff,
  });

  const [{ opacity: fadeIn }, setFifteenMinFade] = useSpring(() => ({ opacity: 0 }));
  useEffect(() => {
    setFifteenMinFade({ opacity: 1, from: { opacity: 0 }, config: config.slow });
  }, [newLinkSettings.allowFifteenMinuteMarkBooking, setFifteenMinFade]);

  const { textScale } = useSpring({
    textScale: createdNewLink ? 1 : 0,
    delay: 200,
    config: config.molasses,
  });

  const scaleInterpolateRange = [0, 0.25, 0.35, 0.45, 0.5, 0.55, 0.65, 0.75, 1];
  const scaleInterpolateOutput = [1.0, 1.008, 1.02, 1.033, 1.037, 1.032, 1.021, 1.01, 1];

  // Meeting Name
  const meetingNameInfo = useSpring({
    opacity: expandMeetingName ? 1 : 0,
    delay: 125,
  });

  // Description
  const descriptionInfo = useSpring({ opacity: expandDescription ? 1 : 0, delay: 50 });

  ////////////////// Misc //////////////////

  const enableSave = !!newLinkSettings.name;
  const bookingTimeBufferHoursToDisplay = newLinkSettings.bookingTimeBufferHours || prevTimeBuffer;

  // Responsive styling
  const removeDivider = windowWidth && windowWidth < 920;
  const showLinkActiveText = !!(windowWidth && windowWidth > 1000);
  const alwaysShowWarningTooltip = !!(windowWidth && windowWidth > 1200);

  const onChangeSlug = (e: ChangeEvent<HTMLInputElement>) => {
    const newSlug = sanitizeSlug(e.target.value);

    const { slugError, blockChange } = validateSlug(newSlug);

    setSlugError(slugError);
    if (blockChange) {
      return; // Short circuit and don't allow the slug to change.
    }

    clearError();
    setLinkSetting("slug", newSlug);
  };

  const onShowMoreOptions = () => {
    trackEvent(TrackingEvents.LINKS.EDIT_PAGE.EXPAND_OPTIONS, {
      meetingSettingsId: currentLinkSettings?.id,
    });
    setShowMore(true);
  };

  const onChangeIsActive = () => {
    const isActive = !newLinkSettings.isActive;
    setLinkSetting("isActive", isActive);
    trackEvent(TrackingEvents.LINKS.EDIT_PAGE.CHANGE_ACTIVE, {
      meetingSettingsId: currentLinkSettings?.id,
      isActive,
    });
  };

  const handleReturnToDashboard = (...args: unknown[]) => {
    if (!hasChanges || window.confirm(CONFIRM_UNSAVED_ONUNLOAD_TEXT)) {
      onReturnToDashboard.apply(null, ...args);
    }
  };

  const hasSlugChanged = newLinkSettings.slug && newLinkSettings.slug !== currentLinkSettings?.slug;
  const warningTooltip = hasSlugChanged ? (
    <div>
      <span className="cw-font-bold">Saving a new link URL inactivates the old URL.</span> If you've
      shared this link, share the new URL with your link recipients.
      <br />
      <br />
      {linkName && currentLinkSettings?.slug && (
        <>
          <div className="cw-text-red-500 cw-bold cw-flex cw-items-center cw-gap-1">
            <Close className="cw-text-20" />
            <span className="cw-line-through cw-text-red-500">
              {getSchedulingLinkUrl(linkName, currentLinkSettings.slug, true)}
            </span>
          </div>
          <div className="cw-text-green-darker cw-bold cw-flex cw-items-center cw-gap-1">
            <Check className="cw-text-20" />
            <span className="cw-text-slate-standard">
              {getSchedulingLinkUrl(linkName, newLinkSettings.slug, true)}
            </span>
          </div>
        </>
      )}
    </div>
  ) : (
    ""
  );

  return (
    <div>
      <animated.div
        className={classNames(classes.top, "cw-sticky cw-top-0 cw-bg-white cw-z-10", {
          "cw-pb-6": createUpdateError || slugError,
        })}
        style={{
          transform: textScale
            .to({
              range: scaleInterpolateRange,
              output: scaleInterpolateOutput,
            })
            .to((x) => `scale(${x})`),
        }}
      >
        {!currentLinkSettings ? (
          <NewLinkTopSection
            onCreateNew={() => {
              onCreateUpdate(newLinkSettings);
            }}
            enableCreateBtn={enableSave && !saving}
            creating={saving}
            onReturnToDashboard={handleReturnToDashboard}
          />
        ) : (
          <>
            <animated.div
              style={{
                ...saveOrViewLink,
              }}
            >
              <div className="cw-flex cw-gap-2">
                <Button
                  className="cw-text-slate-standard"
                  onClick={handleReturnToDashboard}
                  cw-id="back-button"
                >
                  <ArrowBackIos />
                  <span>{hasChanges ? "CANCEL" : "MY LINKS"}</span>
                </Button>
              </div>
            </animated.div>
            <div className={classNames([classes.topBarRight, "cw-ml-4"])}>
              <div
                className={classNames([
                  classes.linkActive,
                  "xxs:cw-flex cw-hidden cw-items-center",
                ])}
              >
                {showLinkActiveText && (
                  <span className="cw-font-body cw-text-standard-grey cw-text-base">
                    Link is Active
                  </span>
                )}
                <Switch
                  color="primary"
                  checked={newLinkSettings.isActive}
                  onChange={onChangeIsActive}
                  cw-id="link-active-checkbox"
                />
              </div>
              <LinkEditor
                linkName={linkName}
                showLinkActiveText={showLinkActiveText}
                slug={newLinkSettings.slug}
                slugError={slugError}
                createUpdateError={createUpdateError}
                onChangeSlug={onChangeSlug}
                setSlugError={setSlugError}
              />
              {hasChanges ? (
                <WarningTooltip
                  title={warningTooltip}
                  open={(alwaysShowWarningTooltip || saveHovered) && enableSave && !saving}
                >
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => onCreateUpdate(newLinkSettings)}
                    disabled={!enableSave || saving}
                    onMouseEnter={() => setWarningTooltipOpen(true)}
                    onMouseLeave={() => setWarningTooltipOpen(false)}
                  >
                    <Check style={{ marginRight: 4 }} />
                    {newLinkSettings.isActive ? "Save and View" : "Save and View All"}
                  </Button>
                </WarningTooltip>
              ) : (
                <Button
                  color="primary"
                  onClick={onPreview}
                  disabled={!newLinkSettings.isActive}
                  cw-id="view-live-page-button"
                >
                  <RemoveRedEye style={{ marginRight: 10 }} />
                  <span style={{ position: "relative" }}></span>
                  VIEW LIVE PAGE
                </Button>
              )}
            </div>
          </>
        )}
      </animated.div>
      <div
        className={classNames([
          classes.bottom,
          { "cw-flex-wrap": removeDivider, "cw-ml-6 xxs:cw-ml-5": removeDivider },
        ])}
      >
        {leftToRightTransition(
          (style, key, _, i) =>
            [
              <animated.div
                className={classNames([classes.left, { "cw-max-w-[470px]": removeDivider }])}
                style={style}
                key={key}
              >
                {leftTransition(
                  (style, key, _, i) =>
                    [
                      <animated.div style={style} key={key} className="cw-mb-3 cw-w-full">
                        <h2
                          className={classes.header}
                          style={{ marginBottom: 24 }}
                          id="define-event-label"
                        >
                          Link info
                        </h2>
                        {expandMeetingName ? (
                          <TextField
                            className={classes.name}
                            placeholder="Meeting name"
                            variant="filled"
                            value={newLinkSettings.name}
                            multiline
                            maxRows={4}
                            fullWidth
                            autoFocus={!currentLinkSettings}
                            inputProps={{
                              autoCorrect: "off",
                              spellCheck: "false",
                              "aria-label": "edit meeting name text field",
                            }}
                            inputRef={titleRef}
                            onBlur={() => {
                              setLinkSetting("name", newLinkSettings.name.trim());
                            }}
                            onFocus={() => {
                              if (titleRef.current) {
                                titleRef.current.setSelectionRange(
                                  newLinkSettings.name.length,
                                  newLinkSettings.name.length,
                                );
                              }
                            }}
                            onChange={({ target: { value: newName } }) => {
                              if (!currentLinkSettings) {
                                const syncedDuration = last(
                                  sortBy(
                                    filter(syncableMeetingNames, (_, durationString) =>
                                      newName.startsWith(durationString),
                                    ),
                                  ),
                                );

                                if (syncedDuration) {
                                  setLinkSetting("timingSettings", {
                                    ...newLinkSettings.timingSettings,
                                    durationMinutes: syncedDuration,
                                  });
                                }
                              }

                              setLinkSetting("name", newName.slice(0, 50)); // Max 50 char name;
                            }}
                            cw-id="link-name"
                          />
                        ) : (
                          <div>
                            <span
                              className="cw-font-brand cw-text-dusk-darker cw-text-2xl cw-font-bold"
                              aria-label="meeting name"
                            >
                              {newLinkSettings.name}{" "}
                              <Button
                                variant="text"
                                onClick={() => setExpandTitle(true)}
                                classes={{
                                  label: "cw-font-sm cw-font-body cw-text-green-darker",
                                  root: "-cw-mt-1 cw-inline",
                                }}
                                aria-label="edit meeting name"
                              >
                                <div className="cw-items-center cw-flex cw-gap-1 cw-text-16 cw-normal-case cw-font-normal">
                                  <Edit fontSize="small" /> Edit title
                                </div>
                              </Button>
                            </span>
                          </div>
                        )}
                        {expandMeetingName && (
                          <animated.div
                            style={meetingNameInfo}
                            className="cw-text-slate-standard cw-font-body cw-text-sm cw-whitespace-normal cw-mt-2"
                          >
                            {`Guests ${
                              currentLinkSettings?.isActive ? "" : "will "
                            }see this title when booking with this link`}
                          </animated.div>
                        )}
                      </animated.div>,
                      <animated.div style={style} key={key} className={classes.timing}>
                        <div className="cw-font-body cw-text-standard-grey">
                          <Timelapse />
                          Let people schedule
                          <Select
                            id="link-duration-select"
                            cw-id="link-duration-dropdown"
                            className={classes.select}
                            MenuProps={{
                              disableScrollLock: true,
                            }}
                            value={newLinkSettings.timingSettings.durationMinutes}
                            autoWidth
                            disableUnderline
                            variant="filled"
                            onChange={({
                              target: { value: newDuration },
                            }: ChangeEvent<{ value: number }>) => {
                              if (!currentLinkSettings) {
                                // Determine if the current meeting name starts with the duration string of the current duration.
                                const currentDurationString = findKey(
                                  syncableMeetingNames,
                                  (duration, durationString) =>
                                    duration === newLinkSettings.timingSettings.durationMinutes &&
                                    newLinkSettings.name.startsWith(durationString),
                                );
                                // Find the duration string we would substitute in. sortBy to sort by durationString ascending
                                // to prioritize replacing with "1 hour" instead of "60 minute".
                                const [[newDurationString]] = sortBy(
                                  Object.entries(syncableMeetingNames).filter(
                                    ([_, d]) => d === newDuration,
                                  ),
                                );
                                if (currentDurationString && newDurationString) {
                                  setLinkSetting(
                                    "name",
                                    newLinkSettings.name.replace(
                                      currentDurationString,
                                      newDurationString,
                                    ),
                                  );
                                }
                              }

                              setLinkSetting("timingSettings", {
                                ...newLinkSettings.timingSettings,
                                durationMinutes: newDuration,
                              });
                            }}
                          >
                            {DURATIONS.map((d) => (
                              <MenuItem key={d} value={d}>
                                {formatDuration(d)}
                              </MenuItem>
                            ))}
                          </Select>
                        </div>
                        <div className="cw-font-body cw-text-standard-grey">
                          <CalendarToday />
                          Within the next
                          <Select
                            id="link-num-days-select"
                            cw-id="link-window-size-days"
                            className={classes.select}
                            MenuProps={{
                              disableScrollLock: true,
                              PaperProps: {
                                style: { maxHeight: 270 },
                              },
                            }}
                            value={newLinkSettings.timingSettings.timeRange.windowSizeDays}
                            autoWidth
                            disableUnderline
                            variant="filled"
                            onChange={(e: ChangeEvent<{ value: number }>) =>
                              setLinkSetting("timingSettings", {
                                ...newLinkSettings.timingSettings,
                                timeRange: {
                                  ...newLinkSettings.timingSettings.timeRange,
                                  windowSizeDays: e.target.value,
                                },
                              })
                            }
                          >
                            {[...Array<undefined>(31)].map((_: undefined, d: number) => {
                              const day = d + 1;
                              return (
                                <MenuItem key={day} value={day}>
                                  {day} {pluralize("day", day)}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </div>
                        <div>
                          <WorkOutline />
                          <span id="during-label" className="cw-font-body cw-text-standard-grey">
                            During your
                          </span>
                          <Select
                            id="link-working-or-meeting-select"
                            cw-id="bookable-hours-type-dropdown"
                            labelId="during-label"
                            className={classes.select}
                            MenuProps={{
                              disableScrollLock: true,
                            }}
                            value={newLinkSettings.bookableHours}
                            autoWidth
                            disableUnderline
                            variant="filled"
                            onChange={(e: ChangeEvent<{ value: BookableHoursEnum }>) =>
                              setLinkSetting("bookableHours", e.target.value)
                            }
                          >
                            <MenuItem
                              key={BookableHoursEnum.MeetingHours}
                              value={BookableHoursEnum.MeetingHours}
                              cw-id="bookable-hours-type-meeting-hours"
                            >
                              Meeting Hours
                            </MenuItem>
                            <MenuItem
                              key={BookableHoursEnum.WorkingHours}
                              value={BookableHoursEnum.WorkingHours}
                              cw-id="bookable-hours-type-working-hours"
                            >
                              Working Hours
                            </MenuItem>
                          </Select>
                        </div>
                        <div className="cw-font-body cw-text-standard-grey">
                          <Videocam />
                          Using
                          <Select
                            id="link-conferencing"
                            className={classes.select}
                            MenuProps={{
                              disableScrollLock: true,
                            }}
                            value={newLinkSettings.locationSettings?.locationType}
                            autoWidth
                            disableUnderline
                            variant="filled"
                            cw-id="location-type-dropdown"
                            onChange={(e) => {
                              if (e.target.value === "ConnectZoom") {
                                window.open(`${getServerUrl()}${paths.zoomSettings}`, "_blank");
                              } else {
                                setLinkSetting("locationSettings", {
                                  ...currentLinkSettings?.locationSettings,
                                  __typename: "LocationSettings",
                                  locationType: e.target.value as LinkLocationType,
                                  locationDetails: null,
                                });
                              }
                            }}
                          >
                            {canUseZoom ? (
                              <MenuItem value={LinkLocationType.Zoom}>
                                <LocationMenuItem locationType={LinkLocationType.Zoom} />
                              </MenuItem>
                            ) : (
                              <MenuItem key="ConnectZoom" value="ConnectZoom">
                                <div className="cw-flex cw-items-center">
                                  <span className="cw-mb-[-4px]">
                                    <ZoomLogo />
                                  </span>
                                  <span className="cw-ml-[7px]">Connect your Zoom account</span>
                                </div>
                              </MenuItem>
                            )}
                            {[LinkLocationType.Meet, LinkLocationType.InDescription].map(
                              (locationType) => (
                                <MenuItem key={locationType} value={locationType}>
                                  <LocationMenuItem
                                    key={locationType}
                                    locationType={locationType}
                                  />
                                </MenuItem>
                              ),
                            )}
                          </Select>
                        </div>
                        <div className="cw-py-3 cw-items-start">
                          <Notes />
                          {!expandDescription ? (
                            <Button
                              onClick={() => setExpandDescription(true)}
                              classes={{
                                label:
                                  "cw-text-slate-standard cw-font-body cw-normal-case cw-font-normal cw-text-base",
                                root: "-cw-ml-2 -cw-mt-2",
                              }}
                              aria-label="add description"
                            >
                              Add description
                            </Button>
                          ) : (
                            <div>
                              <TextareaAutosize
                                autoFocus={!newLinkSettings.description}
                                className={classes.description}
                                placeholder="Add description"
                                rowsMin={2}
                                maxRows={20}
                                value={newLinkSettings.description || ""}
                                onBlur={() => {
                                  setLinkSetting(
                                    "description",
                                    newLinkSettings.description?.trim() ?? null,
                                  );
                                }}
                                onChange={(e) => {
                                  if (e.target.value.length > MAX_DESC_LENGTH) {
                                    return;
                                  }
                                  setLinkSetting("description", e.target.value);
                                }}
                                cw-id="link-description"
                                aria-label="link description"
                              />
                              <animated.div
                                className="cw-text-slate-standard cw-font-body cw-text-sm cw-whitespace-normal cw-mt-1"
                                style={descriptionInfo}
                              >
                                {`This description ${
                                  currentLinkSettings?.description && currentLinkSettings?.isActive
                                    ? "is"
                                    : "will be"
                                } visible to those you share this link with
                                and will be included on any bookings scheduled`}
                              </animated.div>
                            </div>
                          )}
                        </div>
                      </animated.div>,
                    ]?.[i],
                )}
              </animated.div>,
              !removeDivider && (
                <animated.div className={classes.divider} style={style} key={key} />
              ),
              <animated.div className={classes.right} style={style} key={key}>
                {rightTransition((style, key) => {
                  return {
                    // rightTransition iterates over the keys passed into its useTransition hook. this map
                    // maps each key to its corresponding content.
                    "focus-mode": (
                      <animated.div style={style} key={key}>
                        <h2 className={classes.header} id="protect-focus-label">
                          Protect your focus
                        </h2>
                        <CwRadio
                          value={newLinkSettings.availabilityType}
                          className={classes.radios}
                          onChange={(mode) =>
                            setLinkSetting("availabilityType", mode as AvailabilityType)
                          }
                          ariaLabel="Focus Mode Radios"
                        >
                          <TwoRowRadioOption
                            value={AvailabilityType.MaximizeAvailability}
                            header={AVAILABILITY_TEXT[AvailabilityType.MaximizeAvailability]}
                            subHeader="Make all your Focus Time bookable"
                            cw-id="maximize-availability"
                          />
                          <TwoRowRadioOption
                            value={AvailabilityType.BalanceAvailabilityAndFocus}
                            header={AVAILABILITY_TEXT[AvailabilityType.BalanceAvailabilityAndFocus]}
                            subHeader="Let people book slots only at the start or end of Focus Time"
                            cw-id="balance-availability-and-focus"
                          />
                          <TwoRowRadioOption
                            value={AvailabilityType.MaximizeFocusTime}
                            header={AVAILABILITY_TEXT[AvailabilityType.MaximizeFocusTime]}
                            subHeader="Focus Time will not be bookable"
                            cw-id="maximize-focus-time"
                          />
                        </CwRadio>
                      </animated.div>
                    ),
                    "show-more": (
                      <animated.div style={style} key={key}>
                        <Button
                          variant="contained"
                          color="inherit"
                          onClick={onShowMoreOptions}
                          cw-id="more-options-button"
                        >
                          More Options
                        </Button>
                      </animated.div>
                    ),
                    limits: (
                      <animated.div
                        style={{ ...style, marginBottom: 6 }}
                        key={key}
                        id="meeting-limits"
                      >
                        <h2 className={classes.header}>Meeting limits</h2>
                        <div>
                          <FormControlLabel
                            checked={enableBookingTimeBuffer}
                            control={
                              <Checkbox
                                color="primary"
                                cw-id="time-buffer-checkbox"
                                onChange={(e) => {
                                  setEnableBookingTimeBuffer(e.target.checked);
                                  // This will get removed once we have a true boolean to turn this feature on and off.
                                  if (!e.target.checked) {
                                    setPrevTimeBuffer(bookingTimeBufferHoursToDisplay);
                                    setLinkSetting("bookingTimeBufferHours", 0);
                                  } else {
                                    setLinkSetting(
                                      "bookingTimeBufferHours",
                                      bookingTimeBufferHoursToDisplay,
                                    );
                                  }
                                }}
                              />
                            }
                            label="Don't let guests book in the next"
                          />
                          <Select
                            id="booking-time-buffer-select"
                            cw-id="time-buffer-dropdown"
                            className={classes.select}
                            MenuProps={{
                              disableScrollLock: true,
                              PaperProps: {
                                style: { maxHeight: 270 },
                              },
                            }}
                            disabled={!enableBookingTimeBuffer}
                            disableUnderline
                            value={bookingTimeBufferHoursToDisplay}
                            autoWidth
                            variant="filled"
                            onChange={(e: ChangeEvent<{ value: number }>) =>
                              setLinkSetting("bookingTimeBufferHours", e.target.value)
                            }
                          >
                            {[...Array(24).keys()]
                              .map((h) => ++h)
                              .concat([48, 72, 96, 120, 144, 168])
                              .map((h) => (
                                <MenuItem key={h} value={h}>
                                  {h >= 24
                                    ? pluralize("days", h / 24, true)
                                    : pluralize("hour", h, true)}
                                </MenuItem>
                              ))}
                          </Select>
                        </div>
                        <FormControlLabel
                          checked={newLinkSettings.allowFifteenMinuteMarkBooking}
                          control={
                            <Checkbox
                              color="primary"
                              onChange={(e) =>
                                setLinkSetting("allowFifteenMinuteMarkBooking", e.target.checked)
                              }
                              cw-id="fifteen-minute-mark-checkbox"
                            />
                          }
                          label="Allow booking on the 15 minute mark "
                        />
                        <div className={classes.noMeetingDay}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                color="primary"
                                checked={newLinkSettings.bookOnNoMeetingDay}
                                onChange={(e) =>
                                  setLinkSetting("bookOnNoMeetingDay", e.target.checked)
                                }
                                cw-id="no-meeting-day-checkbox"
                              />
                            }
                            label="Let guests book on your no-meeting day"
                          />
                        </div>
                      </animated.div>
                    ),
                    best: (
                      <animated.div
                        style={{ ...style, display: "flex", gap: 20 }}
                        id="highlight-best-times"
                        key={key}
                        cw-id="highlight-best-times-checkbox"
                      >
                        <div>
                          <h2 className={classes.header}>Highlight your best times</h2>
                          <FormControlLabel
                            checked={newLinkSettings.highlightBestTimes}
                            control={
                              <Checkbox
                                color="primary"
                                onChange={(e) =>
                                  setLinkSetting("highlightBestTimes", e.target.checked)
                                }
                              />
                            }
                            label='Highlight your "Best" times'
                          />
                          <div
                            className={classNames([
                              "cw-flex cw-items-center cw-gap-4 cw-text-slate-standard",
                              { "cw-flex-wrap": !showLinkActiveText },
                            ])}
                          >
                            <div className={classes.timeExamples}>
                              <CenteredCard
                                content={
                                  <Typography variant="body2" className={classes.time}>
                                    1:00pm
                                  </Typography>
                                }
                              />
                              <CenteredCard
                                content={
                                  <Typography variant="body2" className={classes.time}>
                                    2:00pm
                                    {newLinkSettings.highlightBestTimes && (
                                      <animated.span
                                        className={classes.bestBadge}
                                        style={bestIconStyle}
                                      >
                                        BEST
                                      </animated.span>
                                    )}
                                  </Typography>
                                }
                              />
                              <CenteredCard
                                content={
                                  <Typography variant="body2" className={classes.time}>
                                    2:
                                    <animated.span style={{ opacity: fadeIn }}>
                                      {newLinkSettings.allowFifteenMinuteMarkBooking ? "15" : "30"}
                                    </animated.span>
                                    pm
                                  </Typography>
                                }
                              />
                            </div>
                            <div className={classes.bestTimes}>
                              Best times according to the Clockwise scheduler. These are highlighted
                              to help guide guests towards times that are more in-line with your
                              preferences
                            </div>
                          </div>
                        </div>
                      </animated.div>
                    ),
                    delete: currentLinkSettings?.id && (
                      <animated.div
                        style={style}
                        key={key}
                        className="cw-mt-4"
                        cw-id="delete-link-btn"
                      >
                        <RedButton
                          disabled={deleting}
                          onClick={() => setDeleteLinkDialogOpen(true)}
                          label="Delete link"
                          cw-id="delete-link-btn"
                        />
                      </animated.div>
                    ),
                  }[key];
                })}
              </animated.div>,
            ]?.[i],
        )}
      </div>
      <CwDialogConfirmRed
        header={deleteHeader}
        body={deleteBody}
        open={deleteLinkDialogOpen}
        onConfirm={() => {
          setDeleteLinkDialogOpen(false);
          if (currentLinkSettings?.id) {
            onDelete(currentLinkSettings?.id);
          }
        }}
        onCancel={() => setDeleteLinkDialogOpen(false)}
        confirmText="Delete"
      />
    </div>
  );
};
